import { api } from '../api';
import { escapeHtml, confirmModal, setButtonBusy } from './shell';
import { t } from '../i18n/i18n';

interface PowerStationImage {
  id: number;
  url: string;
  sortOrder: number;
  createdAt: string;
}

/**
 * Mounts the image-gallery panel into <c>container</c> for an existing power
 * station. The panel lists current photos, supports JPEG/PNG/WebP uploads,
 * per-tile delete, and move-up/move-down reordering. Each mutation re-fetches
 * the canonical order from the server.
 *
 * Only call this when the power station has been persisted (id is known) -
 * the API endpoints all require a station id.
 */
export async function mountImagesPanel(container: HTMLElement, powerStationId: number): Promise<void> {
  container.innerHTML = renderShell();
  const grid = container.querySelector<HTMLDivElement>('.ps-gallery__grid')!;
  const fileInput = container.querySelector<HTMLInputElement>('#ps-image-file')!;
  const uploadBtn = container.querySelector<HTMLButtonElement>('#ps-image-upload-btn')!;
  const errorBox = container.querySelector<HTMLParagraphElement>('#ps-image-error')!;
  const emptyMsg = container.querySelector<HTMLParagraphElement>('.ps-gallery__empty')!;

  let images: PowerStationImage[] = [];

  const showError = (message: string) => {
    errorBox.textContent = message;
    errorBox.hidden = false;
  };
  const clearError = () => {
    errorBox.textContent = '';
    errorBox.hidden = true;
  };

  const render = () => {
    if (images.length === 0) {
      emptyMsg.hidden = false;
      grid.innerHTML = '';
      return;
    }
    emptyMsg.hidden = true;
    grid.innerHTML = images.map((img, idx) => `
      <figure class="ps-gallery__tile" data-image-id="${img.id}">
        <img src="${escapeHtml(img.url)}" alt="Power station photo" loading="lazy"/>
        <div class="ps-gallery__actions">
          <button type="button" class="ps-gallery__btn" data-action="up" ${idx === 0 ? 'disabled' : ''} aria-label="Move up">&uarr;</button>
          <button type="button" class="ps-gallery__btn" data-action="down" ${idx === images.length - 1 ? 'disabled' : ''} aria-label="Move down">&darr;</button>
          <button type="button" class="ps-gallery__btn ps-gallery__btn--danger" data-action="delete" aria-label="Delete">&times;</button>
        </div>
      </figure>
    `).join('');
  };

  const refresh = async () => {
    try {
      const client = await api();
      const { data } = await client.get<PowerStationImage[]>(`/api/admin/power-stations/${powerStationId}/images`);
      images = data;
      render();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load images.');
    }
  };

  grid.addEventListener('click', async (ev) => {
    const target = ev.target as HTMLElement;
    const btn = target.closest<HTMLButtonElement>('button[data-action]');
    if (!btn) return;
    const tile = btn.closest<HTMLElement>('.ps-gallery__tile');
    if (!tile) return;
    const imageId = Number(tile.dataset.imageId);
    if (!Number.isFinite(imageId)) return;

    clearError();
    const action = btn.dataset.action;
    setButtonBusy(btn, true);
    try {
      const client = await api();
      if (action === 'delete') {
        if (!(await confirmModal({
          title: t('admin.confirm.modal_title_delete'),
          message: t('admin.power_station_form.images_confirm_delete'),
          confirmLabel: t('admin.confirm.modal_confirm_delete'),
        }))) return;
        await client.delete(`/api/admin/power-stations/${powerStationId}/images/${imageId}`);
        await refresh();
        return;
      }
      if (action === 'up' || action === 'down') {
        const idx = images.findIndex((i) => i.id === imageId);
        if (idx < 0) return;
        const swap = action === 'up' ? idx - 1 : idx + 1;
        if (swap < 0 || swap >= images.length) return;
        const reordered = images.slice();
        [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
        await client.put(`/api/admin/power-stations/${powerStationId}/images/order`, {
          imageIds: reordered.map((i) => i.id),
        });
        await refresh();
      }
    } catch (err) {
      showError(extractError(err, 'Operation failed.'));
    } finally {
      // The grid re-renders on success, so this only matters on the
      // early-return and error paths where the button instance survives.
      setButtonBusy(btn, false);
    }
  });

  uploadBtn.addEventListener('click', async () => {
    clearError();
    const files = fileInput.files;
    if (!files || files.length === 0) {
      showError('Pick at least one file first.');
      return;
    }

    uploadBtn.disabled = true;
    const originalLabel = uploadBtn.textContent;
    try {
      const client = await api();
      for (let i = 0; i < files.length; i++) {
        uploadBtn.textContent = `Uploading ${i + 1}/${files.length}…`;
        const fd = new FormData();
        fd.append('file', files[i]);
        // axios sees FormData + multipart Content-Type and lets the browser
        // re-emit the header with the correct boundary.
        await client.post(`/api/admin/power-stations/${powerStationId}/images`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      fileInput.value = '';
      await refresh();
    } catch (err) {
      showError(extractError(err, 'Upload failed.'));
    } finally {
      uploadBtn.disabled = false;
      uploadBtn.textContent = originalLabel;
    }
  });

  await refresh();
}

function renderShell(): string {
  return `
    <fieldset class="form-group ps-gallery">
      <legend>Images</legend>
      <p class="ps-gallery__hint">Upload JPEG, PNG, or WebP. Maximum 5 MB per file. First image is shown first on the public map.</p>
      <p id="ps-image-error" class="login-error" hidden></p>
      <div class="ps-gallery__grid" aria-live="polite"></div>
      <p class="ps-gallery__empty">No images yet.</p>
      <div class="ps-gallery__uploader">
        <input type="file" id="ps-image-file" accept="image/jpeg,image/png,image/webp" multiple/>
        <button type="button" id="ps-image-upload-btn" class="btn btn--primary">Upload</button>
      </div>
    </fieldset>`;
}

function extractError(err: unknown, fallback: string): string {
  // Axios error response: surface the server's { message } if present.
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: { message?: string }; status?: number } }).response;
    if (resp?.data?.message) return resp.data.message;
    if (resp?.status === 503) return 'Image storage is not configured. Contact a platform admin.';
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
